import { format } from 'date-fns';
import { ar, es, fr, ka } from 'date-fns/locale';
import en from 'date-fns/locale/en-US';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getWorkerSerializedPeriodDaysArr } from '@/modules/Settlements/components/Details/CrewLevel/MainPayablesDays/logic';
import { CardColumn } from '@/modules/Settlements/components/Details/shared/CardColumn';
import { EmptyDayCard } from '@/modules/Settlements/components/Details/shared/EmptyDayCard';
import { ISettlements } from '@/modules/Settlements/types';
import { Typography } from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';

const WorkerDailyDues: React.FC<{
  days: (ISettlements.IWorkerSettlementDaySchema & { embtyDay?: Date })[];
  startDate: string;
  endDate: string;
}> = ({ days, startDate, endDate }) => {
  const { locale } = useRouter();
  const { t } = useTranslation('common');
  const [selectedAsset] = useStore((store) => store.asset);
  const serializedPeriodDaysArr = useMemo(
    () => getWorkerSerializedPeriodDaysArr(days, startDate, endDate),
    [startDate, endDate, days]
  );

  return (
    <div className='mt-16 flex w-full flex-col gap-16 border-t-xs border-t-neutralVariant-90 py-24'>
      <Typography variant='h2' weight='semiBold'>
        {t('dailyDuesDetails')}
      </Typography>

      {serializedPeriodDaysArr.map((day, index) =>
        // embty date
        day?.embtyDay ? (
          <EmptyDayCard key={index} day={day.embtyDay} />
        ) : (
          // date had issuance
          day?.date && (
            <div className='scrollbar-rounded-lg flex w-full items-center justify-between gap-2 overflow-x-auto whitespace-nowrap rounded-2xl border-xs border-neutralVariant-90 bg-background-neutral-99 px-16 py-24 overflow-y-hidden scrollbar-thin  scrollbar-thumb-background-400 scrollbar-h-1'>
              <CardColumn
                label={t('date')}
                value={format(new Date(day.date), 'EEEE - dd MMM yyyy', {
                  locale:
                    locale === 'ar'
                      ? ar
                      : locale === 'es'
                      ? es
                      : locale === 'fr'
                      ? fr
                      : locale === 'ka'
                      ? ka
                      : en,
                })}
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('total-hours')}
                value={day.totalHours}
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('total-operations')}
                value={day.totalOperations}
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('base-pay')}
                value={
                  <span className='flex items-center gap-1'>
                    <span>
                      {day.basePay} {selectedAsset?.currency}
                    </span>
                    <TippyTooltip
                      placement='bottom'
                      maxWidth={300}
                      content={
                        <Typography
                          variant='caption'
                          weight='semiBold'
                          className='py-8 px-12'
                        >
                          {t('worker-settlement-base-pay-info')}
                        </Typography>
                      }
                    >
                      <span className='text-text-600'>
                        <ExleminationMark />
                      </span>
                    </TippyTooltip>
                  </span>
                }
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('bonus')}
                value={
                  <span>
                    {day.totalBonuses} {selectedAsset?.currency}
                  </span>
                }
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('deduction')}
                value={
                  <span>
                    {day.totalDeductions} {selectedAsset?.currency}
                  </span>
                }
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('OVERTIME')}
                value={
                  <span>
                    {day.totalOvertime} {selectedAsset?.currency}
                  </span>
                }
                labelVariant='subtitle2'
              />
              <CardColumn
                label={t('total-pay')}
                value={
                  <span>
                    {day.totalPay} {selectedAsset?.currency}
                  </span>
                }
                labelVariant='subtitle2'
              />
            </div>
          )
        )
      )}
    </div>
  );
};
export { WorkerDailyDues };
